import { useEffect, useState } from "react";
import { ChevronsUpDown, Search, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";

export default function EmployeePicker({ value, onChange, testid = "employee-picker", autoOpen = false, placeholder = "Search employee by name or code…", source = "/employees", hideBalance = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOpen(autoOpen);
  }, [autoOpen]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(source, { params: { q, limit: 40 } })
        .then((r) => setItems(r.data))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, source]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 h-12 text-left hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-600/30 transition-colors"
        >
          {value ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="money text-xs bg-blue-50 text-blue-800 rounded px-1.5 py-0.5 font-semibold">{value.code}</span>
              <span className="truncate font-medium text-slate-900">{value.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-slate-500 text-sm"><Search className="h-4 w-4" />{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[300px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type name or employee code…" value={q} onValueChange={setQ} data-testid={`${testid}-input`} />
          <CommandList>
            {loading && <div className="py-4 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
            {!loading && <CommandEmpty>No employee found.</CommandEmpty>}
            <CommandGroup>
              {items.map((e) => (
                <CommandItem
                  key={e.code}
                  value={e.code}
                  onSelect={() => { onChange(e); setOpen(false); setQ(""); }}
                  data-testid={`${testid}-option-${e.code}`}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="money text-xs text-blue-800 font-semibold">{e.code}</span>
                    <span className="truncate">{e.name}</span>
                    {e.status === "inactive" && <span className="text-[10px] text-slate-500 border rounded px-1">Inactive</span>}
                  </span>
                  {!hideBalance && <span className={`money text-xs ${e.balance > 0 ? "text-red-700" : "text-slate-400"}`}>{e.has_ledger ? inr(e.balance) : "No ledger"}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
